export const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

// Bump whenever the banner text or the list of tools changes
export const CONSENT_VERSION = 1;

// Banner text version sent with every recorded consent
export const ANALYTICS_TEXT_VERSION = `analytics-v${CONSENT_VERSION}`;

// Our Cookie Policy commits to asking again every 6 months
export const CONSENT_MAX_AGE_MS = 183 * 24 * 60 * 60 * 1000;

export type ConsentValue = 'true' | 'rejected' | null;

type StoredConsent = {
  value: 'true' | 'rejected';
  date: number;
  version: number;
};

const listeners = new Set<() => void>();

const isStoredConsent = (data: unknown): data is StoredConsent => {
  if (typeof data !== 'object' || data === null) return false;
  if (!('value' in data && 'date' in data && 'version' in data)) return false;
  return (
    (data.value === 'true' || data.value === 'rejected') &&
    typeof data.date === 'number' &&
    typeof data.version === 'number'
  );
};

// Old bare values, another version or an expired answer all count as no answer
export const getCookieConsent = (): ConsentValue => {
  if (globalThis.window === undefined) return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredConsent(parsed)) return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (Date.now() - parsed.date > CONSENT_MAX_AGE_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
};

// `date` is when the choice was made, e.g. an answer restored from a server
export const setCookieConsent = (
  value: StoredConsent['value'],
  date: number = Date.now(),
) => {
  const stored: StoredConsent = {
    value,
    date,
    version: CONSENT_VERSION,
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(stored));
  listeners.forEach((listener) => listener());
};

// Usable outside React (e.g. before Sentry.init); also fires on changes made in other tabs
export const subscribeCookieConsent = (listener: () => void) => {
  listeners.add(listener);

  const onStorage = (event: StorageEvent) => {
    if (event.key === COOKIE_CONSENT_KEY || event.key === null) listener();
  };
  globalThis.addEventListener?.('storage', onStorage);

  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', onStorage);
  };
};
